(function (g: any) {
  'use strict';

  /* ══════════════════════════════════════════════════════════════════════════
     THE SCENE — one stack, one law.

     Everything open stands in one scene: a stack ordered by opening, over the
     day as implicit ground. Escape, the close glyph and browser Back all
     address the top of this stack, and what stands beneath is exactly what
     they reveal. Opening and closing are decided here, by pure rules over
     plain data — never by a hand-composed teardown inside a component
     (that is what made closing Crafts kill a standing project, made the
     Settings × skip Tabs, and let a closed record resurrect its parent).

     This module is deliberately DOM-free, React-free and history-free:
     the loop's release gate is the model. Wiring arrives in later stories;
     tests/scene.test.js is the law's text and tests/scene-matrix.test.js
     walks its whole space.
     ══════════════════════════════════════════════════════════════════════════ */

  type Kind = 'pane-screen' | 'window' | 'sheet' | 'dialog' | 'menu' | 'frame';
  type Row = {
    kind: Kind;
    host: 'panel' | 'body';
    group?: string;
    parent?: string;
    /* The navigation home. Group exclusivity never removes it: destinations
       open OVER Tabs, and closing them must reveal it — that is M-2. */
    home?: boolean;
    suppressesPeek?: boolean;
    /* An open peek must not retract beneath this surface: everything the day
       opens full-screen INSIDE ITSELF, which would otherwise take itself
       away the moment the pointer wandered off the panel.
       Inside itself is the whole of the test, and it is about the HOST, not
       the size. A window in the workspace is the board's neighbour: it stands
       beside the day, well above the peeked panel's plane, and a peek held
       open beneath one is a day nobody can see being kept out by the window
       covering it. Four workspace windows carried this flag until 2026-08-28,
       and the retract simply did not happen while any of them stood. */
    holdsPeek?: boolean;
    /* Phone scroll containment (N-11). `phonePage`: a Tabs-child page that
       takes ownership of the document scroller — real page movement, so the
       browser chrome still collapses. `locksScroll`: a real viewport layer
       that locks the document behind it, offset kept. Not exclusive: a
       catalogue stays the page beneath while its selected window locks. */
    phonePage?: boolean;
    locksScroll?: boolean;
    /* This surface docks the workspace slot beside the panel column on a
       wide canvas without OCCUPYING it — presence, not exclusivity. The
       windows dock by kind; the catalogue docks by this flag, because giving
       it the workspace group made it an occupant, and an occupant evicts:
       opening Databases closed a standing Projects window unasked. */
    docks?: boolean;
    plane?: number;
  };
  type Entry = { id: string; params?: Record<string, unknown> };
  type Scene = { stack: Entry[] };

  /* The registry: every navigable surface, its shape and its manners.
     Planes name the ledger value where the surface lives on <body>
     (md-sheet-size.ts documents that ledger); in-panel surfaces stack
     inside the isolated app root and carry their in-root value. */
  const REGISTRY: Record<string, Row> = {
    tabs:       { kind: 'pane-screen', host: 'panel', group: 'panel', home: true },
    settings:   { kind: 'pane-screen', host: 'panel', group: 'panel', plane: 90, holdsPeek: true, phonePage: true },
    crafts:     { kind: 'pane-screen', host: 'panel', group: 'panel', plane: 90, holdsPeek: true, phonePage: true },
    /* The catalogue renders in the panel column and DOCKS the workspace slot
       — but it does not occupy it. It once carried the workspace group for
       its standing (Settings over it must not retire it), and the group's
       exclusivity came riding along: opening Databases evicted a standing
       Projects window, the M-1 disease through a registry row instead of a
       teardown. The standing never needed a group — dbwindow stands the same
       way with none — so the catalogue keeps only the docking. */
    catalogue:  { kind: 'pane-screen', host: 'panel', docks: true, plane: 90, holdsPeek: true, phonePage: true },
    desks:      { kind: 'pane-screen', host: 'panel', group: 'panel', plane: 280, phonePage: true },
    /* The canvas both locks (it is a real viewport layer) and keeps the page
       mechanism active beneath it — group exclusivity retires the chooser
       when the canvas opens, and the page geometry must not go away with it. */
    board:      { kind: 'pane-screen', host: 'panel', group: 'panel', plane: 285, phonePage: true, locksScroll: true },
    grid:       { kind: 'pane-screen', host: 'panel', group: 'panel', plane: 60, holdsPeek: true, locksScroll: true },
    /* The workspace's own windows do NOT hold the peek — see `holdsPeek`. */
    workshop:   { kind: 'window', host: 'body', group: 'workspace', plane: 140, locksScroll: true },
    project:    { kind: 'window', host: 'body', group: 'workspace', parent: 'workshop', plane: 145, locksScroll: true },
    database:   { kind: 'window', host: 'body', group: 'workspace', plane: 140, locksScroll: true },
    /* The database window opened FROM the catalogue (D3). Its own surface,
       standing over the catalogue without retiring it: no group, so the
       workspace exclusivity cannot reach the catalogue beneath; no parent,
       because the catalogue can close under it and the window survives.
       Its × and the catalogue's close are two different dismiss targets. */
    dbwindow:   { kind: 'window', host: 'body', plane: 140, locksScroll: true },
    compose:    { kind: 'sheet',  host: 'body', suppressesPeek: true, locksScroll: true, plane: 151 },
    /* These two HOLD rather than suppress — they carried suppressesPeek by
       anticipation once, and it was a masked error: a reschedule opened from
       a peeked day must keep the panel beneath it, not take it away. */
    reschedule: { kind: 'sheet',  host: 'body', holdsPeek: true, locksScroll: true, plane: 151 },
    confirm:    { kind: 'dialog', host: 'body', holdsPeek: true, locksScroll: true, plane: 200 },
    /* Riders: surfaces the App does not own, seated through the dismiss
       arbiter's registration (see useMdDismissLayer's `surface`). A picker
       or a column menu is as much an open thing as a window — one Back, one
       Escape, one dismissal each. */
    datepicker: { kind: 'menu', host: 'body', plane: 200 },
    menu:       { kind: 'menu', host: 'body' },
    /* A database row's page (side peek or full page) — the deepest rider:
       it opens over whichever surface holds the table, and one Back closes
       the page, never the database beneath it. */
    rowsheet:   { kind: 'sheet', host: 'body' },
    /* The New-project beginning sheet — the Workshop's own sheet, seated by
       its dismiss layer like any rider. Its close ladder confirms a dirty
       draft, so a Back that lands on the confirm re-seats and bounces. */
    begin:      { kind: 'sheet', host: 'body' },
    /* Frames that ride (N-14). A screen pushed inside a standing host gets
       one journal entry per level above the host's ground, so browser Back
       steps a sub-view out before it can touch the host — exactly what
       Escape does. Deeper levels share the one entry and bounce (the
       stacked-rider rule); the parent cascades the whole walk when the host
       is dismissed. One row per host so interleaved walks keep their order. */
    'settings-frame': { kind: 'frame', host: 'panel', parent: 'settings' },
    'workshop-frame': { kind: 'frame', host: 'body', parent: 'workshop' },
    'project-frame':  { kind: 'frame', host: 'body', parent: 'project' },
  };

  const mdSceneRegistry = (): Record<string, Row> => REGISTRY;
  const mdSceneEmpty = (): Scene => ({ stack: [] });
  const row = (id: string): Row | null => REGISTRY[id] || null;

  const mdSceneTop = (scene: Scene): string | null =>
    scene.stack[scene.stack.length - 1]?.id ?? null;

  const mdSceneIsOpen = (scene: Scene, id: string): boolean =>
    scene.stack.some((entry) => entry.id === id);

  /* The phone's scroll containment, read off the standing scene (N-11).
     `page`: a Tabs-child page owns the document scroller. `locked`: a real
     viewport layer locks the document behind it. A set, not a mode — the
     page mechanism stays active beneath a lock so that dismissing the layer
     reveals a page whose geometry never went away. */
  const mdScenePhoneScroll = (scene: Scene): { page: boolean; locked: boolean } => {
    /* The lock belongs to a viewport layer covering the document's owner, so
       ORDER decides: a lock at or above the last page locks (the D3 window
       over its catalogue; the Desk canvas, which is both at once); a lock
       buried BENEATH the page on top does not — the shaded workshop under a
       standing catalogue must not freeze the very page the user is reading. */
    let lastPage = -1;
    let lastLock = -1;
    scene.stack.forEach((entry, at) => {
      const r = row(entry.id);
      if (r?.phonePage) lastPage = at;
      if (r?.locksScroll) lastLock = at;
    });
    return { page: lastPage >= 0, locked: lastLock >= 0 && lastLock >= lastPage };
  };

  /* The peek's three inputs, read off the standing scene (N-10). `held`: an
     open peek must not retract — something the day opened full-screen inside
     itself stands. `suppressed`: the record sheet stands, and the sentence
     being written outranks the glance. `covered`: a window stands over the
     day, so a caret left inside it is no longer a reason to keep it out. A
     surface states its peek behaviour in its registry row or has none;
     nobody keeps a list. */
  const mdScenePeek = (scene: Scene): { held: boolean; suppressed: boolean; covered: boolean } => {
    let held = false;
    let suppressed = false;
    let covered = false;
    for (const entry of scene.stack) {
      const r = row(entry.id);
      if (r?.holdsPeek) held = true;
      if (r?.suppressesPeek) suppressed = true;
      /* `covered`: a WINDOW stands in the workspace, over the day rather than
         inside it. Derived from what a window IS — no third flag to forget on
         the next row — and no registry row is both a window and something the
         day opens inside itself, so the two answers cannot argue.
         What reads it: the peek's focus clause. A caret left in the day is
         where you WERE when you opened the window, and nothing in the app
         moves it afterwards, so the day stood out behind the window until it
         was closed. Sheets the day raises over itself (planes 151, 200) are
         not windows and do not cover it. */
      if (r?.kind === 'window') covered = true;
    }
    return { held, suppressed, covered };
  };

  /* X depends on Y when walking X's registry parent chain reaches Y. A
     dependent cannot outlive what it stands on — dismissing the Workshop
     takes the project standing on it; dismissing the project leaves the
     Workshop exactly where it stood. */
  function dependsOn(id: string, on: string): boolean {
    let walk = row(id)?.parent;
    for (let depth = 0; walk && depth < 8; depth += 1) {
      if (walk === on) return true;
      walk = row(walk)?.parent;
    }
    return false;
  }

  /* What a dismiss(id) removes: the surface and its dependents, in stack
     order. Never a neighbour — that is M-1. */
  function mdSceneDismissSet(scene: Scene, id: string): string[] {
    if (!mdSceneIsOpen(scene, id)) return [];
    return scene.stack
      .filter((entry) => entry.id === id || dependsOn(entry.id, id))
      .map((entry) => entry.id);
  }

  function mdSceneDismiss(scene: Scene, id: string): Scene {
    const away = new Set(mdSceneDismissSet(scene, id));
    if (!away.size) return scene;
    return { stack: scene.stack.filter((entry) => !away.has(entry.id)) };
  }

  /* Escape / Back address the top and only the top. */
  const mdScenePop = (scene: Scene): Scene => {
    const top = mdSceneTop(scene);
    return top ? mdSceneDismiss(scene, top) : scene;
  };

  function mdSceneOpen(scene: Scene, id: string, params?: Record<string, unknown> | null): Scene {
    const shape = row(id);
    if (!shape) return scene;
    const top = scene.stack[scene.stack.length - 1];
    if (top && top.id === id) {
      const same = JSON.stringify(top.params || null) === JSON.stringify(params || null);
      if (same) return scene;
    }
    /* Exclusivity by registry group: opening a surface retires the group
       members it replaces — and their dependents — but never the navigation
       home, never its own parent chain, and never another group. The home
       guard binds everyone EXCEPT the home itself: a surface always retires
       its own standing entry, or re-opening Tabs over a workspace would
       stand a second copy and a later dismissal would take both at once. */
    const away = new Set<string>();
    for (const entry of scene.stack) {
      const standing = row(entry.id);
      if (!standing) continue;
      const sameSurface = entry.id === id;
      const sameGroup = !!shape.group && standing.group === shape.group;
      const isOwnAncestor = dependsOn(id, entry.id);
      if ((sameSurface || (sameGroup && !standing.home)) && !isOwnAncestor) {
        for (const gone of mdSceneDismissSet(scene, entry.id)) away.add(gone);
      }
    }
    /* THE STACK IS THE PROVENANCE — nothing is seated beneath an opening
       surface. A project opened from search stands over search, and its
       dismiss reveals search; fabricating a Workshop beneath it is exactly
       how a closed thing summons a parent nobody opened (M-3's disease).
       The registry parent chain serves the dismiss cascade, which only ever
       applies when both truly stand. */
    const stack = scene.stack.filter((entry) => !away.has(entry.id));
    return { stack: [...stack, { id, params: params || undefined }] };
  }

  /* Navigating to the Workshop root. A standing project is never torn down by
     being sent back to what it stands on (the stale-closure bug, as law): the
     project stays, the Workshop beneath it resets to its root — and if the
     project stood alone, the Workshop is seated BENEATH it, the one deliberate
     seating in the model, because the alternative is destruction. */
  function mdSceneWorkshopRoot(scene: Scene): Scene {
    const at = scene.stack.findIndex((entry) => entry.id === 'project');
    if (at < 0) return mdSceneOpen(scene, 'workshop');
    if (mdSceneIsOpen(scene, 'workshop'))
      return { stack: scene.stack.map((entry) => entry.id === 'workshop' ? { id: 'workshop' } : entry) };
    return { stack: [...scene.stack.slice(0, at), { id: 'workshop' }, ...scene.stack.slice(at)] };
  }

  /* ── places ────────────────────────────────────────────────────────────────
     The navigation grammar (md-navigation.ts) resolved into scene changes.
     This is goFind's thirteen branches as one table. Layout is an input,
     never a fork: the stack is the same on a phone and on the desk — only
     presentation differs, and presentation lives with the components. */
  function mdScenePlaceActions(place: any, scene: Scene, _layout?: any, _source?: string | null):
    { scene: Scene; opened: string | null } {
    if (!place || typeof place !== 'object') return { scene, opened: null };
    /* A destination pulls you out of a standing panel screen: choosing a
       place IS leaving Settings or Crafts, whichever way it was reached. The
       workspace, by contrast, is only ever displaced by the registry's group
       rule — no destination hand-closes it, which is M-1 as a shape rather
       than a fix. Panel destinations therefore keep a docked workspace from
       ANY source; the old rule demanded source === 'tabs' and tore the
       workspace down for every other door. */
    const shed = (from: Scene, ids: string[]): Scene =>
      ids.reduce((acc, id) => mdSceneDismiss(acc, id), from);
    const base = shed(scene, ['settings', 'crafts']);
    const open = (from: Scene, id: string, params?: Record<string, unknown> | null) =>
      ({ scene: mdSceneOpen(from, id, params), opened: id });
    switch (place.go) {
      /* Home. The ground carries no stack. */
      case 'day': return { scene: mdSceneEmpty(), opened: null };
      case 'workshop':
      case 'projects':
        return { scene: mdSceneWorkshopRoot(base), opened: 'workshop' };
      case 'pursuit':
      case 'domain':
        /* A walk to a pursuit or domain page — the Workshop's own pages. A
           standing project is replaced: this is walking somewhere, not being
           unwound back to what the project stands on. */
        return open(base, 'workshop', { view: { kind: place.go, id: place.id } });
      case 'project': return open(base, 'project', { id: place.id });
      case 'database': return open(base, 'database', { id: place.id, row: place.row || null });
      case 'databases': return open(base, 'catalogue');
      case 'settings': return open(scene, 'settings', { view: place.view || 'root' });
      case 'crafts': return open(scene, 'crafts', place.id ? { id: place.id } : null);
      case 'boards': return open(scene, 'desks');
      case 'board': return open(base, 'board', { id: place.id });
      case 'record': return open(base, 'compose', { ref: place.ref });
      case 'view':
        if (place.scope === 'account') return { scene: mdSceneWorkshopRoot(base), opened: 'workshop' };
        if (place.scope === 'project') return open(base, 'project', { id: place.projectId });
        if (place.scope === 'database') return open(base, 'database', { id: place.databaseId, row: null });
        return { scene, opened: null };
      default: return { scene, opened: null };
    }
  }

  /* ── the phone's presentation ─────────────────────────────────────────────
     Layout never forks the stack. On one-column layouts a panel screen and a
     body-host workspace window cannot share the screen, and the ledger says
     the window would paint over the screen above it in the scene — so the
     presentation SHADES the workspace (visibility, never teardown) while a
     panel screen stands above it. Tabs does not count: it is the home the
     workspace was opened over, not a cover. */
  function mdSceneWorkspaceShaded(scene: Scene): boolean {
    let lastWorkspace = -1;
    let lastPanelScreen = -1;
    scene.stack.forEach((entry, at) => {
      const shape = row(entry.id);
      if (!shape) return;
      /* Windows count by kind: dbwindow and its kin carry no group, and the
         catalogue no longer does — a groupless window under a panel screen
         still needs the shade. */
      if (shape.group === 'workspace' || shape.kind === 'window') lastWorkspace = at;
      else if (shape.kind === 'pane-screen' && !shape.home) lastPanelScreen = at;
    });
    return lastWorkspace >= 0 && lastPanelScreen > lastWorkspace;
  }

  /* ── the panel reveal ──────────────────────────────────────────────────────
     After a panel screen is dismissed: does the day panel hand back to Tabs
     or to the day? Read from what truly stands, never guessed from where the
     close came from — that guess was the D4 defect, and the guess's ban was
     M-2. A docked workspace window keeps Tabs beside it, as it always did;
     `docked` is an input until N-5 moves the workspace into the scene. */
  const mdScenePanelReveal = (scene: Scene, docked: boolean): 'tabs' | 'day' =>
    (mdSceneTop(scene) === 'tabs' || docked) ? 'tabs' : 'day';

  /* ── the docked workspace ──────────────────────────────────────────────────
     Does this scene hold a surface that docks the workspace slot beside the
     panel column on a wide canvas? A scene question, deliberately: the App's
     render-time copy of this fact answers for the scene BEFORE a dismissal,
     and that stale answer told Tabs it was still a side-destination after
     the workshop beside it had closed. The windows dock (dbwindow carries no
     group — it stands over its host — so kind catches it), and so does the
     catalogue, by its `docks` flag now that it no longer carries the
     workspace group. */
  const mdSceneDocksWorkspace = (scene: Scene): boolean =>
    scene.stack.some((entry) => {
      const shape = row(entry.id);
      return !!shape && (shape.group === 'workspace' || shape.kind === 'window' || !!shape.docks);
    });

  /* ── the desk shroud ───────────────────────────────────────────────────────
     What the Desk layers mean for the panel Tabs left behind. Tabs hides
     beneath a desk layer and raises an opaque guard so the day never flashes
     under the handoff — but the guard is a statement about what the layout
     SHOWS. The chooser owns the whole screen everywhere ('covered'). The
     canvas owns it on a one-column layout, where the day is a full-width
     sheet over the board and Tabs hiding bare would show it. Everywhere the
     desk is GROUND — a wide canvas, and now a phone — the guard is wrong:
     the board holds still UNDER what stands on it, so a guard blacks out the
     very thing being presented ('bare'). On a wide canvas that was the
     empty-side-panel defect of 2026-08-24; on a phone it is newer, and it
     arrived with the desk. The phone desk used to be a modal layer portalled
     ABOVE Tabs, so covering everything beneath it was right. It is ground
     now, painted beneath Tabs, and the guard fell over the desk itself —
     choosing a desk from the chooser landed on a black screen with nothing
     but the countdown on it (walked on an iPhone, 2026-08-28). Nothing is
     left flashing by the change: the desk is opaque, full-screen and already
     mounted, so there is no moment where the day is what would show. A scene
     question, deliberately: the desk layers close through setBoardLayer,
     the arbiter and the journal, and none of those doors speaks to Tabs —
     the App restates this fact on every transition instead, so no door can
     forget to announce. */
  const mdSceneDeskShroud = (scene: Scene, layout: any): 'covered' | 'bare' | 'none' => {
    if (!mdSceneIsOpen(scene, 'tabs')) return 'none';
    if (mdSceneIsOpen(scene, 'desks')) return 'covered';
    if (!mdSceneIsOpen(scene, 'board')) return 'none';
    if (!layout) return 'covered';
    return (layout.phone || (layout.canvas && !layout.overlay)) ? 'bare' : 'covered';
  };

  /* ── history ───────────────────────────────────────────────────────────────
     The browser's stack mirrors this one: every gesture is one entry. The
     delta between two scenes says what the history controller owes the
     browser — and dismissal never waits on the browser to answer (the D1
     law); it reconciles after. */
  function mdSceneHistoryDelta(before: Scene, after: Scene):
    { op: 'push' | 'back' | 'replace' | 'none'; count?: number } {
    const a = before.stack.map((entry) => entry.id);
    const b = after.stack.map((entry) => entry.id);
    if (a.length === b.length && a.every((id, i) => id === b[i])) {
      const paramsMoved = JSON.stringify(before.stack) !== JSON.stringify(after.stack);
      return paramsMoved ? { op: 'replace' } : { op: 'none' };
    }
    const prefix = (short: string[], long: string[]) => short.every((id, i) => id === long[i]);
    if (b.length < a.length && prefix(b, a)) return { op: 'back', count: a.length - b.length };
    /* Removals only, top untouched — a MIDDLE dismissal, like the catalogue
       closing beneath the standing D3 window. Not a navigation (a push here
       would make Back re-open what was just closed) and not a walk (the top
       still stands): the standing entry is rewritten in place. */
    const subsequence = (short: string[], long: string[]) => {
      let at = 0;
      for (const id of long) if (id === short[at]) at += 1;
      return at === short.length;
    };
    if (b.length < a.length && subsequence(b, a)) return { op: 'replace' };
    /* Everything else is a navigation the browser gets one entry for: a
       plain open, a compound open (a wiki link seating Workshop and project
       in one gesture), and a SWAP — settings→crafts, workshop→database. A
       swap deepens the stack because the browser remembers the state it
       replaced: Back re-shows it. (N-1 called the swap a replace; the walk
       "Back ×2 lands on the day" is what corrected the reading.) */
    return { op: 'push', count: Math.max(1, b.length - a.length) };
  }

  /* ── the journal ───────────────────────────────────────────────────────────
     The controller's memory: the scene that stood at every history entry
     this session wrote, indexed by the `mdDepth` stamped on the entry. The
     era refuses a dead session's stamps — after a reload the numbers still
     sit on old entries, and answering them from a fresh journal would apply
     somebody else's scene. Pure over plain data; the browser calls live in
     md-app, which only ever asks these two questions. */
  type Journal = { era: string; scenes: Scene[]; pointer: number };
  const mdSceneJournalStart = (scene: Scene, era: string): Journal =>
    ({ era, scenes: [scene], pointer: 0 });

  /* An app-driven scene change, folded in. Answers what the browser is owed:
     push one entry, rewrite the standing one, walk back `count` real steps —
     or nothing, for a close whose surface never had an entry (a boot-restored
     window standing on depth zero). The journal moves BEFORE the walk is
     taken: state first, history after (D1). */
  function mdSceneJournalRecord(journal: Journal, scene: Scene):
    { journal: Journal; op: 'push' | 'replace' | 'back' | 'none'; count: number } {
    const before = journal.scenes[journal.pointer] || mdSceneEmpty();
    const delta = mdSceneHistoryDelta(before, scene);
    if (delta.op === 'none') return { journal, op: 'none', count: 0 };
    /* A DISMISSAL EDITS THE PAST. The surfaces this change closed no longer
       "truly stand beneath" anything: every scene at or behind the landing
       pointer loses them, or the next Back would stand a closed surface
       back up (the resurrection the whole spine exists to end). Forward
       scenes keep them — walking forward onto a surface's own entry is a
       deliberate re-opening, and a push (a swap, a navigation) scrubs
       nothing: Back after a swap re-shows what the swap replaced. */
    const kept = new Set(scene.stack.map((entry) => entry.id));
    const gone = new Set(before.stack.map((entry) => entry.id)
      .filter((id) => !kept.has(id)));
    const scrub = (scenes: Scene[], upTo: number): Scene[] => (!gone.size ? scenes
      : scenes.map((s, i) => (i > upTo || !s.stack.some((entry) => gone.has(entry.id)) ? s
        : { stack: s.stack.filter((entry) => !gone.has(entry.id)) })));
    /* Only a NEW push truncates the forward scenes — exactly the browser's
       own law. A back or a replace leaves them standing, because the browser
       keeps its forward entries too, and a Forward walk must find the scene
       that stood there (for a surface on the bare day URL there is no URL to
       fall back to). */
    if (delta.op === 'replace') {
      const scenes = scrub(journal.scenes.slice(), journal.pointer);
      scenes[journal.pointer] = scene;
      return { journal: { ...journal, scenes }, op: 'replace', count: 0 };
    }
    if (delta.op === 'push') {
      const scenes = [...journal.scenes.slice(0, journal.pointer + 1), scene];
      return { journal: { ...journal, scenes, pointer: journal.pointer + 1 }, op: 'push', count: 1 };
    }
    const steps = Math.min(delta.count || 1, journal.pointer);
    const pointer = journal.pointer - steps;
    const scenes = scrub(journal.scenes.slice(), pointer);
    scenes[pointer] = scene;
    return { journal: { ...journal, scenes, pointer }, op: 'back', count: steps };
  }

  /* The browser walked onto an entry (popstate). Answer with the scene that
     stood there — idempotently, so a swallowed back() self-heals — or null
     for an entry this journal never wrote: the caller falls back to the URL,
     never to a teardown. */
  function mdSceneJournalPop(journal: Journal, state: unknown):
    { journal: Journal; scene: Scene } | null {
    if (!state || typeof state !== 'object') return null;
    const depth = (state as any).mdDepth;
    if ((state as any).mdEra !== journal.era) return null;
    if (typeof depth !== 'number' || !Number.isInteger(depth) ||
      depth < 0 || depth >= journal.scenes.length) return null;
    const scene = journal.scenes[depth];
    if (!scene) return null;
    return { journal: { ...journal, pointer: depth }, scene };
  }

  Object.assign(g, {
    mdSceneRegistry, mdSceneEmpty, mdSceneTop, mdSceneIsOpen, mdScenePeek, mdScenePhoneScroll,
    mdSceneOpen, mdSceneDismiss, mdSceneDismissSet, mdScenePop,
    mdSceneWorkshopRoot, mdScenePlaceActions, mdScenePanelReveal,
    mdSceneDocksWorkspace, mdSceneDeskShroud,
    mdSceneWorkspaceShaded, mdSceneHistoryDelta,
    mdSceneJournalStart, mdSceneJournalRecord, mdSceneJournalPop,
  });
})(typeof window !== 'undefined' ? window : globalThis);
